import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import moment from "moment";
import { useSelector, shallowEqual } from "react-redux";
import {
  OverlayTrigger,
  Tooltip,
  Badge,
  Button as BootstrapButton,
} from "react-bootstrap";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  makeStyles,
} from "@material-ui/core";
import {
  Card,
  CardBody,
  CardHeader,
  CardHeaderToolbar,
} from "../../../_metronic/_partials/controls";
import { PauseEnrollmentDialog } from "../FamilyProfile/Widgets/PauseEnrollmentDialog";
import { QuickEmailModal } from "./QuickEmailModal";

// Adapt a flat dashboard row to the `course` shape PauseEnrollmentDialog reads
// (student_batch_id + a batches[] carrying day/active_id/pause dates). A row with
// active_id 6 drives the Resume date picker / Extend; active_id 1 (upcoming) makes
// Resume a cancel.
const toCourse = (r) => ({
  student_batch_id: r.student_batch_id,
  batches: [
    {
      day: r.day,
      active_id: r.active_id,
      pause_start: r.pause_start,
      pause_end: r.pause_end,
    },
  ],
});
// Buckets shown top-to-bottom (most urgent first). Keys match the server's
// pause_state (GET api/reports/pause/dashboard/:branchId).
const STATES = [
  { key: "overdue", label: "Overdue returns", color: "danger" },
  { key: "on_break", label: "On break", color: "warning" },
  { key: "upcoming", label: "Upcoming", color: "primary" },
];

function KpiCard({ label, value, color }) {
  return (
    <div className="col">
      <div className="card card-custom gutter-b" style={{ minHeight: "115px" }}>
        <div className="card-body">
          <div className={`text-${color} font-weight-bolder font-size-h1`}>
            {value}
          </div>
          <div className="text-muted font-weight-bold font-size-lg mt-1">
            {label}
          </div>
        </div>
      </div>
    </div>
  );
}

// "2026-07-06" -> "Jul 6, 2026"; "18:30:00" -> "6:30 PM". Blank stays blank.
const fmtDate = (v) => (v ? moment(v, "YYYY-MM-DD").format("MMM D, YYYY") : "");
const fmtTime = (v) =>
  v ? moment(v, ["HH:mm:ss", "HH:mm"]).format("h:mm A") : "";

// Curated columns for the dashboard — pause tracking only. `nowrap` keeps a cell
// on one line; `render` formats the raw value.
const COLUMNS = [
  { key: "student_user_id", label: "Student ID", align: "center", nowrap: true },
  { key: "student_name", label: "Student" },
  { key: "parent_name", label: "Parent" },
  { key: "parent_mobile", label: "Mobile", nowrap: true },
  { key: "course_abbr", label: "Stream", nowrap: true },
  { key: "day", label: "Day", nowrap: true },
  {
    key: "start_time",
    label: "Time",
    align: "center",
    nowrap: true,
    render: (r) => fmtTime(r.start_time),
  },
  {
    key: "pause_start",
    label: "Pause Start",
    align: "center",
    nowrap: true,
    render: (r) => fmtDate(r.pause_start),
  },
  {
    key: "pause_end",
    label: "Return Date",
    align: "center",
    nowrap: true,
    render: (r) => fmtDate(r.pause_end),
  },
  { key: "days_break", label: "Break Days", align: "center", nowrap: true },
  { key: "pause_reason", label: "Reason" },
];

// Map a column's align hint to the MUI TableCell `align` prop.
const muiAlign = (c) =>
  c.align === "center" ? "center" : c.align === "right" ? "right" : "left";

const useTableStyles = makeStyles((theme) => ({
  tableWrapper: { overflowX: "auto" },
  table: { minWidth: 750 },
  sectionCell: {
    fontWeight: 700,
    fontSize: "1.08rem",
    paddingTop: theme.spacing(1.5),
    paddingBottom: theme.spacing(1.5),
  },
}));

// A single MUI table renders all buckets (via section header rows) so every
// column lines up across sections — matching the Material-UI table look used in
// the Batches area (centered cells, hover rows, thin row borders, pill badges).
function PauseTable({ sections, branchId, authHeaders, onChanged }) {
  const classes = useTableStyles();
  const [dialogMode, setDialogMode] = useState(null); // "resume" | "extend" | null
  const [activeRow, setActiveRow] = useState(null);
  const [emailRow, setEmailRow] = useState(null);

  const openDialog = (row, mode) => {
    setActiveRow(row);
    setDialogMode(mode);
  };
  const closeDialog = () => {
    setDialogMode(null);
    setActiveRow(null);
  };

  const rowActions = (r) => (
    <div className="text-nowrap">
      <OverlayTrigger
        overlay={
          <Tooltip id={`email-${r.enrollment_pause_id}`}>
            {r.parent_email ? "Email parent" : "No parent email on file"}
          </Tooltip>
        }
      >
        <span className="d-inline-block mr-2">
          <BootstrapButton
            className="btn-icon btn-sm btn-secondary btn-hover-primary"
            onClick={() => setEmailRow(r)}
            disabled={!r.parent_email}
            style={!r.parent_email ? { pointerEvents: "none" } : undefined}
          >
            <i className="fas fa-envelope p-0"></i>
          </BootstrapButton>
        </span>
      </OverlayTrigger>
      <OverlayTrigger
        overlay={
          <Tooltip id={`resume-${r.enrollment_pause_id}`}>
            Resume enrollment
          </Tooltip>
        }
      >
        <BootstrapButton
          className="mr-2 btn-icon btn-sm btn-secondary btn-hover-success"
          onClick={() => openDialog(r, "resume")}
        >
          <i className="fas fa-play p-0"></i>
        </BootstrapButton>
      </OverlayTrigger>
      <OverlayTrigger
        overlay={
          <Tooltip id={`extend-${r.enrollment_pause_id}`}>Extend pause</Tooltip>
        }
      >
        <BootstrapButton
          className="btn-icon btn-sm btn-secondary btn-hover-primary"
          onClick={() => openDialog(r, "extend")}
        >
          <i className="fas fa-clock p-0"></i>
        </BootstrapButton>
      </OverlayTrigger>
    </div>
  );

  return (
    <div className={classes.tableWrapper}>
      <Table className={classes.table} aria-labelledby="pauseDashboard">
        <TableHead>
          <TableRow>
            {COLUMNS.map((c) => (
              <TableCell key={c.key} align={muiAlign(c)} className="text-nowrap">
                {c.label}
              </TableCell>
            ))}
            <TableCell align="right" className="text-nowrap">
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sections.map((s) => (
            <React.Fragment key={s.key}>
              <TableRow className={`bg-light-${s.color}`}>
                <TableCell
                  colSpan={COLUMNS.length + 1}
                  className={`${classes.sectionCell} text-${s.color}`}
                >
                  {s.label}
                  <Badge pill variant={s.color} className="ml-2">
                    {s.rows.length}
                  </Badge>
                </TableCell>
              </TableRow>
              {s.rows.map((r) => (
                <TableRow hover key={r.enrollment_pause_id}>
                  {COLUMNS.map((c) => (
                    <TableCell
                      key={c.key}
                      align={muiAlign(c)}
                      className={c.nowrap ? "text-nowrap" : undefined}
                    >
                      {c.render ? c.render(r) : r[c.key]}
                    </TableCell>
                  ))}
                  <TableCell align="right">{rowActions(r)}</TableCell>
                </TableRow>
              ))}
            </React.Fragment>
          ))}
        </TableBody>
      </Table>

      {activeRow && (
        <PauseEnrollmentDialog
          show={!!dialogMode}
          setShow={(v) => {
            if (!v) closeDialog();
          }}
          mode={dialogMode}
          course={toCourse(activeRow)}
          studentName={activeRow.student_name}
          setRefetch={(flag) => {
            if (flag) onChanged();
          }}
        />
      )}

      <QuickEmailModal
        show={!!emailRow}
        onHide={() => setEmailRow(null)}
        to={emailRow ? emailRow.parent_email : ""}
        parentEmails={emailRow ? emailRow.parent_emails : ""}
        parentContacts={emailRow ? emailRow.parent_contacts : ""}
        studentName={emailRow ? emailRow.student_name : ""}
        branchId={branchId}
        authHeaders={authHeaders}
      />
    </div>
  );
}

export default function EnrollmentPauseDashboardPage() {
  const branchId = useSelector((state) => {
    const b = state.auth.branches && state.auth.branches.currentBranch;
    // -1 is the app-wide "no branch selected" sentinel; normalize it to null.
    return b && b.branch_id !== -1 ? b.branch_id : null;
  }, shallowEqual);
  const user_id = useSelector(
    (state) => state.auth.user && state.auth.user[0].user_id,
    shallowEqual
  );
  // Attach the token explicitly on every request. The global axios interceptor
  // is disabled and axios.defaults only gets x-auth-token once getUserByToken()
  // runs during rehydrate — so on a hard reload these fetches would otherwise
  // race that saga and go out unauthenticated (401). Read it from the persisted
  // store like the rest of the app does.
  const authToken = useSelector((state) => state.auth.authToken, shallowEqual);
  const authHeaders = { headers: { "x-auth-token": authToken } };

  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [activating, setActivating] = useState(false);
  const [message, setMessage] = useState("");

  // Super-admin flag gates the activate button. The server enforces the role
  // on both endpoints regardless — this is just UI.
  useEffect(() => {
    if (!user_id || !authToken) return;
    axios
      .get("api/user/role/" + user_id, authHeaders)
      .then((res) =>
        setIsSuperAdmin(
          (res.data || []).some(
            (r) => r.role_id === 1 || r.role_name === "Super Admin"
          )
        )
      )
      .catch(() => setIsSuperAdmin(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user_id, authToken]);

  const fetchDashboard = useCallback(async () => {
    if (!branchId || !authToken) return;
    setLoading(true);
    setError("");
    setMessage(""); // clear any stale activate result when (re)loading a branch
    try {
      const res = await axios(
        "api/reports/pause/dashboard/" + branchId,
        authHeaders
      );
      setRows(res.data.results || []);
    } catch (e) {
      setError(
        e.response && e.response.status === 403
          ? "You don't have access to the pause dashboard."
          : "Could not load the pause dashboard."
      );
      setRows([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId, authToken]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const activate = async () => {
    setActivating(true);
    setMessage("");
    try {
      const res = await axios.post(
        "api/enrollments/activate-due-pauses",
        null,
        authHeaders
      );
      const n = (res.data && res.data.data && res.data.data.flipped) || 0;
      setMessage(
        n > 0
          ? `Activated ${n} pause${n === 1 ? "" : "s"} across all branches.`
          : "Nothing to activate — everything is already up to date."
      );
      await fetchDashboard();
    } catch (e) {
      setMessage("Could not activate pauses.");
    } finally {
      setActivating(false);
    }
  };

  const counts = STATES.reduce((acc, s) => {
    acc[s.key] = rows.filter((r) => r.pause_state === s.key).length;
    return acc;
  }, {});
  const pending = rows.filter((r) => r.needs_activation === 1).length;

  return (
    <>
      <div className="row">
        {STATES.map((s) => (
          <KpiCard key={s.key} label={s.label} value={counts[s.key] || 0} color={s.color} />
        ))}
        <KpiCard label="Pending activation" value={pending} color="dark" />
      </div>

      <Card>
        <CardHeader title="Enrollment Pause Dashboard">
          <CardHeaderToolbar>
            {isSuperAdmin && (
              <button
                type="button"
                className="btn btn-primary"
                onClick={activate}
                disabled={activating}
                title="Flips every branch's due pauses to On Break (global)."
              >
                {activating
                  ? "Activating…"
                  : "Activate due pauses now (all branches)"}
              </button>
            )}
          </CardHeaderToolbar>
        </CardHeader>
        <CardBody>
          {!branchId && (
            <div className="text-muted">Select a branch to view its pauses.</div>
          )}
          {error && <div className="text-danger">{error}</div>}
          {message && <div className="text-info mb-3">{message}</div>}
          {loading && <div className="text-muted">Loading…</div>}
          {!loading && !error && branchId && rows.length === 0 && (
            <div className="text-muted">No open pauses for this branch.</div>
          )}
          {!loading &&
            !error &&
            rows.length > 0 &&
            (() => {
              const sections = STATES.map((s) => ({
                ...s,
                rows: rows.filter((r) => r.pause_state === s.key),
              })).filter((s) => s.rows.length);
              if (!sections.length) return null;
              return (
                <PauseTable
                  sections={sections}
                  branchId={branchId}
                  authHeaders={authHeaders}
                  onChanged={fetchDashboard}
                />
              );
            })()}
        </CardBody>
      </Card>
    </>
  );
}
